/**
 * Video utilities for TyreLink marketplace
 * Provides helpers for video management, validation, and display
 */

// Supported video formats (MIME types)
export const SUPPORTED_VIDEO_FORMATS = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime'
] as const

// Video format extensions
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'] as const

// Maximum video file sizes (in MB)
export const VIDEO_MAX_SIZE = 500 // 500MB for standard videos
export const VIDEO_PREVIEW_MAX_SIZE = 100 // 100MB for preview videos

// Video quality presets
export enum VideoQuality {
  LOW = '480p',
  STANDARD = '720p',
  HIGH = '1080p',
  ULTRA = '2K'
}

// Video categories/types
export enum VideoType {
  INSTALLATION = 'installation',
  PRODUCT_DEMO = 'product_demo',
  FITTING_GUIDE = 'fitting_guide',
  WORKSHOP_TOUR = 'workshop_tour',
  CUSTOMER_TESTIMONIAL = 'customer_testimonial',
  COMPARISON = 'comparison',
  MAINTENANCE = 'maintenance'
}

interface VideoMetadata {
  duration?: number // in seconds
  resolution?: string // e.g., "1920x1080"
  bitrate?: number // in kbps
  format?: string
  thumbnail?: string
}

interface VideoFile {
  id: string
  url: string
  title?: string
  description?: string
  type: VideoType
  metadata?: VideoMetadata
  uploadedAt?: Date
  duration?: number
}

/**
 * Validate if a file is a supported video format
 */
export const isValidVideoFormat = (file: File): boolean => {
  return SUPPORTED_VIDEO_FORMATS.includes(file.type as any) ||
    VIDEO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
}

/**
 * Validate video file size
 */
export const isValidVideoSize = (file: File, maxSizeMB: number = VIDEO_MAX_SIZE): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  return file.size <= maxSizeBytes
}

/**
 * Get file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Get video duration from file (client-side)
 */
export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const objectUrl = URL.createObjectURL(file)
    
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(video.duration)
    }
    
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Could not load video metadata'))
    }
    
    video.src = objectUrl
  })
}

/**
 * Format duration in seconds to human-readable format
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Generate video thumbnail from URL using a poster frame
 * This is a client-side approach using video element
 */
export const getVideoThumbnail = (videoUrl: string, time: number = 1): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }
    
    video.crossOrigin = 'anonymous'
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.currentTime = time
    }
    
    video.oncanplay = () => {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/jpeg'))
    }
    
    video.onerror = () => {
      reject(new Error('Could not load video'))
    }
    
    video.src = videoUrl
  })
}

/**
 * Validate and sanitize video URL
 */
export const isValidVideoUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    // Check if URL protocol is http or https
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Get video type label for display
 */
export const getVideoTypeLabel = (type: VideoType): string => {
  const labels: Record<VideoType, string> = {
    [VideoType.INSTALLATION]: 'Installation Guide',
    [VideoType.PRODUCT_DEMO]: 'Product Demo',
    [VideoType.FITTING_GUIDE]: 'Fitting Guide',
    [VideoType.WORKSHOP_TOUR]: 'Workshop Tour',
    [VideoType.CUSTOMER_TESTIMONIAL]: 'Customer Testimonial',
    [VideoType.COMPARISON]: 'Comparison',
    [VideoType.MAINTENANCE]: 'Maintenance Guide'
  }
  return labels[type] || 'Video'
}

/**
 * Check if video URL is from a CDN (Supabase storage)
 */
export const isSupabaseCdnUrl = (url: string): boolean => {
  return url.includes('supabase.co') || url.includes('supabaseusercontent.com')
}

/**
 * Generate CDN URL for different video buckets
 */
export const generateCdnVideoUrl = (
  bucket: 'product-videos' | 'business-videos' | 'tyre-videos' | 'rim-videos',
  filePath: string,
  projectUrl: string = import.meta.env.VITE_SUPABASE_URL
): string => {
  // Remove leading slash if present
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath
  return `${projectUrl}/storage/v1/object/public/${bucket}/${cleanPath}`
}

/**
 * Validate multi-video upload
 */
export const validateVideoFiles = (
  files: File[],
  maxVideos: number = 5,
  maxSizeMB: number = VIDEO_MAX_SIZE
): { valid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  if (files.length > maxVideos) {
    errors.push(`Maximum ${maxVideos} videos allowed`)
  }
  
  files.forEach((file, index) => {
    if (!isValidVideoFormat(file)) {
      errors.push(`File ${index + 1}: Unsupported format`)
    }
    if (!isValidVideoSize(file, maxSizeMB)) {
      errors.push(`File ${index + 1}: Exceeds ${maxSizeMB}MB limit`)
    }
  })
  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Create a video object for inventory/products
 */
export const createVideoObject = (
  url: string,
  type: VideoType = VideoType.PRODUCT_DEMO,
  title?: string,
  duration?: number
): VideoFile => {
  return {
    id: crypto.getRandomValues(new Uint8Array(16)).toString(),
    url,
    type,
    title,
    duration,
    uploadedAt: new Date()
  }
}

/**
 * Extract video ID from various video URL formats
 */
export const extractVideoId = (url: string): string | null => {
  try {
    // For YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)
    if (youtubeMatch) return youtubeMatch[1]
    
    // For Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
    if (vimeoMatch) return vimeoMatch[1]
    
    // For local/CDN URLs, return the last part of the path
    const path = new URL(url).pathname
    return path.split('/').pop() || null
  } catch {
    return null
  }
}

/**
 * Check if URL is a YouTube video
 */
export const isYouTubeUrl = (url: string): boolean => {
  return /(?:youtube\.com|youtu\.be)/.test(url)
}

/**
 * Check if URL is a Vimeo video
 */
export const isVimeoUrl = (url: string): boolean => {
  return /vimeo\.com/.test(url)
}

/**
 * Get embedded video URL for YouTube
 */
export const getEmbeddedYouTubeUrl = (url: string): string => {
  const videoId = extractVideoId(url)
  if (!videoId) return url
  return `https://www.youtube.com/embed/${videoId}`
}

/**
 * Get embedded video URL for Vimeo
 */
export const getEmbeddedVimeoUrl = (url: string): string => {
  const videoId = extractVideoId(url)
  if (!videoId) return url
  return `https://player.vimeo.com/video/${videoId}`
}

/**
 * Convert video URL to embeddable format
 */
export const getEmbeddableVideoUrl = (url: string): string => {
  if (isYouTubeUrl(url)) {
    return getEmbeddedYouTubeUrl(url)
  }
  if (isVimeoUrl(url)) {
    return getEmbeddedVimeoUrl(url)
  }
  return url
}

/**
 * Video compression settings for different quality levels
 */
export const videoCompressionSettings = {
  [VideoQuality.LOW]: {
    resolution: '854x480',
    bitrate: '500k',
    codec: 'libx264'
  },
  [VideoQuality.STANDARD]: {
    resolution: '1280x720',
    bitrate: '2500k',
    codec: 'libx264'
  },
  [VideoQuality.HIGH]: {
    resolution: '1920x1080',
    bitrate: '5000k',
    codec: 'libx264'
  },
  [VideoQuality.ULTRA]: {
    resolution: '2560x1440',
    bitrate: '8000k',
    codec: 'libx265'
  }
}

export type { VideoFile, VideoMetadata }
